import { Fragment, type RefObject } from 'react';
import type { BoosterKind } from '../../../../domain/catalog/boosters';
import { RUNNER_LANE_MAX, RUNNER_SHOW_HITBOX } from '../../../../domain/runner/runnerConstants';
import type {
  RunnerBoosterPickup,
  RunnerDecorSprite,
  RunnerHitbox,
  RunnerLevel,
  RunnerObstacle,
  RunnerSpark,
} from '../../../../domain/runner/runnerTypes';
import { LEVEL_GAMEPLAY_Z_INDEX } from './gameplayConstants';
import type { LevelCanvasLayout } from './layout/runnerLevelLayout';
import { projectSvgPointInWorld } from './layout/runnerLevelLayout';
import type { PlayfieldMotionBindings } from './playfieldMotionBindings';
import {
  LEVEL_COLLECTIBLE_ICON_URL,
  LEVEL_ITEM_SHINE_URL,
  LEVEL_SHIELD_BOOSTER_ICON_URL,
  LEVEL_SPEED_BOOSTER_ICON_URL,
  getDecorSpriteUrl,
  getObstacleSpriteUrl,
  getStaticObstacleUrl,
} from './levelVisualAssets';
import './LevelWorldSprites.css';
import './LevelHitboxOverlay.css';

type WorldSpriteProps = {
  entityId?: string;
  motionBindings?: RefObject<PlayfieldMotionBindings | null>;
  svgX: number;
  svgY: number;
  layout: LevelCanvasLayout;
  widthPx: number;
  heightPx: number;
  src: string;
  zIndex: number;
  opacity?: number;
  /** Точка спрайта (0..1), которая ставится в (svgX, svgY); по умолчанию центр. */
  anchor?: { x: number; y: number };
  /** Если задано — спрайт непрерывно вращается на 360°, период оборота в секундах. */
  spinDurationSec?: number;
  /** true — против часовой. */
  spinReverse?: boolean;
  /** Сдвиг фазы вращения, сек. */
  spinDelaySec?: number;
  /** Если задано — спрайт плавно покачивается вверх-вниз, период в секундах. */
  wobbleDurationSec?: number;
};

function WorldSprite({
  entityId,
  motionBindings,
  svgX,
  svgY,
  layout,
  widthPx,
  heightPx,
  src,
  zIndex,
  opacity = 1,
  anchor,
  spinDurationSec,
  spinReverse,
  spinDelaySec,
  wobbleDurationSec,
}: WorldSpriteProps) {
  const { leftPx, topPx } = projectSvgPointInWorld(svgX, svgY, layout);
  const anchorXPct = (anchor?.x ?? 0.5) * 100;
  const anchorYPct = (anchor?.y ?? 0.5) * 100;
  // Path-moving entities: left/top owned by RAF (React re-renders would reset pose).
  const positionStyle =
    entityId != null
      ? {
          position: 'absolute' as const,
          width: widthPx,
          height: heightPx,
          transform: `translate(-${anchorXPct}%, -${anchorYPct}%)`,
          zIndex,
          opacity,
          pointerEvents: 'none' as const,
        }
      : {
          position: 'absolute' as const,
          left: leftPx,
          top: topPx,
          width: widthPx,
          height: heightPx,
          transform: `translate(-${anchorXPct}%, -${anchorYPct}%)`,
          zIndex,
          opacity,
          pointerEvents: 'none' as const,
        };
  const bindEntityEl = (el: HTMLElement | null) => {
    if (entityId == null || motionBindings?.current == null) {
      return;
    }
    if (el == null) {
      motionBindings.current.entities.delete(entityId);
      return;
    }
    const isNew = !motionBindings.current.entities.has(entityId);
    motionBindings.current.entities.set(entityId, el);
    if (isNew) {
      el.style.left = `${leftPx}px`;
      el.style.top = `${topPx}px`;
    }
  };
  if (spinDurationSec == null && wobbleDurationSec == null) {
    return (
      <img
        ref={entityId != null ? bindEntityEl : undefined}
        src={src}
        alt=""
        draggable={false}
        style={positionStyle}
      />
    );
  }
  const animationClassName =
    spinDurationSec != null
      ? spinReverse
        ? 'level-world-sprite__spin level-world-sprite__spin--ccw'
        : 'level-world-sprite__spin'
      : 'level-world-sprite__wobble';
  const animationDurationSec = spinDurationSec ?? wobbleDurationSec;
  return (
    <div ref={entityId != null ? bindEntityEl : undefined} style={positionStyle}>
      <img
        src={src}
        alt=""
        draggable={false}
        className={animationClassName}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          animationDuration: `${animationDurationSec}s`,
          ...(spinDelaySec != null ? { animationDelay: `${spinDelaySec}s` } : null),
          ...(spinDurationSec != null
            ? { transformOrigin: `${anchorXPct}% ${anchorYPct}%` }
            : null),
        }}
      />
    </div>
  );
}

function laneToSvgY(lane: number, viewBoxHeight: number): number {
  return (RUNNER_LANE_MAX - lane) * viewBoxHeight;
}

function spriteSizePx(hitboxWidthS: number, hitboxHeightLane: number, level: RunnerLevel, layout: LevelCanvasLayout) {
  return {
    widthPx: hitboxWidthS * layout.displayScale,
    heightPx: hitboxHeightLane * level.viewBox.height * layout.displayScale,
  };
}

function renderHitboxDebug(
  id: string,
  hitbox: RunnerHitbox,
  level: RunnerLevel,
  layout: LevelCanvasLayout,
  kind: 'obstacle' | 'spark' | 'booster',
) {
  if (!RUNNER_SHOW_HITBOX) {
    return null;
  }
  const svgX = level.startPointSvgX + hitbox.centerS;
  const svgY = laneToSvgY(hitbox.centerLane, level.viewBox.height);
  const { leftPx, topPx } = projectSvgPointInWorld(svgX, svgY, layout);
  const { widthPx, heightPx } = spriteSizePx(hitbox.widthS, hitbox.heightLane, level, layout);
  return (
    <div
      key={`${id}-hitbox`}
      className={`level-hitbox-overlay__box level-hitbox-overlay__box--${kind}`}
      style={{
        left: leftPx,
        top: topPx,
        width: widthPx,
        height: heightPx,
        zIndex: LEVEL_GAMEPLAY_Z_INDEX.DEBUG_HITBOX,
      }}
    />
  );
}

function renderDecor(
  decor: RunnerDecorSprite,
  level: RunnerLevel,
  layout: LevelCanvasLayout,
  motionBindings?: RefObject<PlayfieldMotionBindings | null>,
) {
  const opacity = decor.opacity ?? 1;
  if (opacity <= 0) {
    return null;
  }
  const { widthPx, heightPx } = spriteSizePx(
    decor.widthS,
    decor.heightLane,
    level,
    layout,
  );
  const svgX = level.startPointSvgX + decor.centerS;
  const svgY = laneToSvgY(decor.centerLane, level.viewBox.height);
  const zIndex =
    decor.layer === 'front'
      ? LEVEL_GAMEPLAY_Z_INDEX.FX_FRONT
      : LEVEL_GAMEPLAY_Z_INDEX.DECORS;
  const trackMotion = decor.motion != null;
  return (
    <WorldSprite
      key={decor.id}
      entityId={trackMotion ? decor.id : undefined}
      motionBindings={trackMotion ? motionBindings : undefined}
      svgX={svgX}
      svgY={svgY}
      layout={layout}
      widthPx={widthPx}
      heightPx={heightPx}
      src={getDecorSpriteUrl(decor.spriteKey)}
      zIndex={zIndex}
      opacity={opacity}
      anchor={decor.anchor}
      spinDurationSec={decor.spinDurationSec}
      spinReverse={decor.spinReverse}
      spinDelaySec={decor.spinDelaySec}
      wobbleDurationSec={decor.wobbleDurationSec}
    />
  );
}

function renderObstacle(
  obstacle: RunnerObstacle,
  level: RunnerLevel,
  layout: LevelCanvasLayout,
  motionBindings?: RefObject<PlayfieldMotionBindings | null>,
) {
  const widthS = obstacle.visualWidthS ?? obstacle.hitbox.widthS;
  const heightLane = obstacle.visualHeightLane ?? obstacle.hitbox.heightLane;
  const centerS = obstacle.visualCenterS ?? obstacle.hitbox.centerS;
  const centerLane = obstacle.visualCenterLane ?? obstacle.hitbox.centerLane;
  const { widthPx, heightPx } = spriteSizePx(widthS, heightLane, level, layout);
  const svgX = level.startPointSvgX + centerS;
  const svgY = laneToSvgY(centerLane, level.viewBox.height);
  const src =
    obstacle.spriteKey != null
      ? getObstacleSpriteUrl(obstacle.spriteKey)
      : getStaticObstacleUrl(level.giantId);
  const zIndex =
    obstacle.layer === 'front'
      ? LEVEL_GAMEPLAY_Z_INDEX.OBSTACLES_FRONT
      : LEVEL_GAMEPLAY_Z_INDEX.OBSTACLES;
  const trackMotion = obstacle.motion != null;
  return (
    <Fragment key={obstacle.id}>
      <WorldSprite
        entityId={trackMotion ? obstacle.id : undefined}
        motionBindings={trackMotion ? motionBindings : undefined}
        svgX={svgX}
        svgY={svgY}
        layout={layout}
        widthPx={widthPx}
        heightPx={heightPx}
        src={src}
        zIndex={zIndex}
        opacity={obstacle.opacity ?? 1}
        wobbleDurationSec={obstacle.wobbleDurationSec}
        spinDurationSec={obstacle.spinDurationSec}
      />
      {renderHitboxDebug(obstacle.id, obstacle.hitbox, level, layout, 'obstacle')}
    </Fragment>
  );
}

function renderSpark(spark: RunnerSpark, level: RunnerLevel, layout: LevelCanvasLayout) {
  const { widthPx, heightPx } = spriteSizePx(
    spark.hitbox.widthS,
    spark.hitbox.heightLane,
    level,
    layout,
  );
  const svgX = level.startPointSvgX + spark.hitbox.centerS;
  const svgY = laneToSvgY(spark.hitbox.centerLane, level.viewBox.height);
  return (
    <Fragment key={spark.id}>
      <WorldSprite
        svgX={svgX}
        svgY={svgY}
        layout={layout}
        widthPx={widthPx}
        heightPx={heightPx}
        src={LEVEL_COLLECTIBLE_ICON_URL}
        zIndex={LEVEL_GAMEPLAY_Z_INDEX.SPARKS}
      />
      {renderHitboxDebug(spark.id, spark.hitbox, level, layout, 'spark')}
    </Fragment>
  );
}

function boosterIcon(kind: BoosterKind): string {
  if (kind === 'shield') {
    return LEVEL_SHIELD_BOOSTER_ICON_URL;
  }
  if (kind === 'speed') {
    return LEVEL_SPEED_BOOSTER_ICON_URL;
  }
  return LEVEL_SHIELD_BOOSTER_ICON_URL;
}

/** Shine sits behind the booster icon, sized off the icon's larger side. */
const BOOSTER_SHINE_SCALE = 1.8;

function renderBooster(
  booster: RunnerBoosterPickup,
  level: RunnerLevel,
  layout: LevelCanvasLayout,
) {
  const { widthPx, heightPx } = spriteSizePx(
    booster.hitbox.widthS,
    booster.hitbox.heightLane,
    level,
    layout,
  );
  const svgX = level.startPointSvgX + booster.hitbox.centerS;
  const svgY = laneToSvgY(booster.hitbox.centerLane, level.viewBox.height);
  const shineSizePx = Math.max(widthPx, heightPx) * BOOSTER_SHINE_SCALE;
  return (
    <Fragment key={booster.id}>
      <WorldSprite
        svgX={svgX}
        svgY={svgY}
        layout={layout}
        widthPx={shineSizePx}
        heightPx={shineSizePx}
        src={LEVEL_ITEM_SHINE_URL}
        zIndex={LEVEL_GAMEPLAY_Z_INDEX.BOOSTERS}
      />
      <WorldSprite
        svgX={svgX}
        svgY={svgY}
        layout={layout}
        widthPx={widthPx}
        heightPx={heightPx}
        src={boosterIcon(booster.kind)}
        zIndex={LEVEL_GAMEPLAY_Z_INDEX.BOOSTERS}
      />
      {renderHitboxDebug(booster.id, booster.hitbox, level, layout, 'booster')}
    </Fragment>
  );
}

type LevelWorldSpritesProps = {
  level: RunnerLevel;
  layout: LevelCanvasLayout;
  motionBindings?: RefObject<PlayfieldMotionBindings | null>;
};

export function LevelWorldSprites({
  level,
  layout,
  motionBindings,
}: LevelWorldSpritesProps) {
  return (
    <>
      {level.decors.map((decor) => renderDecor(decor, level, layout, motionBindings))}
      {level.sparks
        .filter((s) => !s.consumed)
        .map((spark) => renderSpark(spark, level, layout))}
      {level.boosters
        .filter((b) => !b.consumed)
        .map((booster) => renderBooster(booster, level, layout))}
      {level.obstacles
        .filter((o) => !o.consumed && (o.opacity ?? 1) > 0)
        .map((obstacle) => renderObstacle(obstacle, level, layout, motionBindings))}
    </>
  );
}
